import React, { useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import dayjs from 'dayjs';
import {
  PageHeader,
  StatsCards,
  Button,
  StatCardSkeleton,
  SearchInput,
  DatePicker,
  TabSelector,
  DataTable,
  DataTableCard,
  Pagination2,
  ColumnSelector,
  TableSkeleton,
} from '../../common';
import type { TableColumn } from '../../common/Table';
import { getSurveyColumns, getTemplateColumns } from './tableColumns';
import { CREATION_STATS_CARDS } from './utils';
import {
  DeleteConfirmModal,
  DuplicateTemplateModal,
  DeleteItemType,
} from './components';
import CreateSurveyModal, { SurveyWizardMode, SurveyWizardEntity } from '../builder/CreateSurveyModal';
import { useSurveyManagement } from './hooks';
import type { PublishedSurvey, DraftSurvey, TemplateItem } from './types';

type ManagementTabKey = 'published' | 'drafts';

const managementTabs = [
  { k: 'published' as const, label: 'Published' },
  { k: 'drafts' as const, label: 'Drafts' },
];

/** Figma Survey Creation: stats + lifecycle tabs + surveys table + template library. */
const SurveyManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ManagementTabKey>('published');
  const [wizard, setWizard] = useState<{
    mode: SurveyWizardMode;
    entityType?: SurveyWizardEntity;
    entityId?: string;
  } | null>(null);

  const openWizard = (mode: SurveyWizardMode, entityType?: SurveyWizardEntity, entityId?: string) => {
    setWizard({ mode, entityType, entityId });
  };
  const {
    statistics, statsLoading, hasError, refreshAll,
    filters, searchInput, updateSearch,
    updateFromDate, updateToDate,
    publishedSurveys, publishedLoading, publishedPage, publishedTotal, publishedLimit,
    setPublishedPage, setPublishedLimit,
    drafts, draftsLoading, draftsPage, draftsTotal, draftsLimit,
    setDraftsPage, setDraftsLimit,
    templates, templatesLoading, templatesPage, templatesTotal, templatesLimit,
    setTemplatesPage, setTemplatesLimit,
    handleToggleSurveyStatus, actionLoading, deleteModal, openDeleteModal,
    closeDeleteModal, confirmDelete, duplicateModal, openDuplicateModal,
    closeDuplicateModal, confirmDuplicate, resetFilters
  } = useSurveyManagement(activeTab);
  const [pubVisibleKeys, setPubVisibleKeys] = useState<string[]>(['questions', 'responses', 'status']);
  const [draftVisibleKeys, setDraftVisibleKeys] = useState<string[]>(['questions', 'responses', 'status']);
  const [tplVisibleKeys, setTplVisibleKeys] = useState<string[]>(['questions', 'usage']);

  useEffect(() => {
    resetFilters();
  }, [activeTab]);

  const toggleKeys = (setter: React.Dispatch<React.SetStateAction<string[]>>) => (key: string) => {
    setter((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const publishedColumns = useMemo<TableColumn<PublishedSurvey>[]>(
    () =>
      getSurveyColumns<PublishedSurvey>('published', {
        onView: (id) => openWizard('view', 'survey', id),
        onToggleStatus: (id) => {
          const s = publishedSurveys.find((x) => x._id === id);
          if (s) handleToggleSurveyStatus(s._id, !s.isActive);
        },
        onDelete: (id, title) => openDeleteModal('published', id, title),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [publishedSurveys]
  );

  const draftColumns = useMemo<TableColumn<DraftSurvey>[]>(
    () =>
      getSurveyColumns<DraftSurvey>('draft', {
        onView: (id) => openWizard('view', 'draft', id),
        onEdit: (id) => openWizard('edit', 'draft', id),
        onDelete: (id, title) => openDeleteModal('draft', id, title),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const templateColumns = useMemo<TableColumn<TemplateItem>[]>(
    () =>
      getTemplateColumns({
        onView: (id) => openWizard('view', 'template', id),
        onEdit: (id) => openWizard('edit', 'template', id),
        onDuplicate: (id, title) => openDuplicateModal(id, title),
        onDelete: (id, title) => openDeleteModal('template', id, title),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const pubVisible = useMemo(
    () => publishedColumns.filter((c) => c.required || pubVisibleKeys.includes(c.key)),
    [publishedColumns, pubVisibleKeys]
  );
  const draftVisible = useMemo(
    () => draftColumns.filter((c) => c.required || draftVisibleKeys.includes(c.key)),
    [draftColumns, draftVisibleKeys]
  );
  const tplVisible = useMemo(
    () => templateColumns.filter((c) => c.required || tplVisibleKeys.includes(c.key)),
    [templateColumns, tplVisibleKeys]
  );

  return (
    <div className="w-full">
      <PageHeader
        title="Survey Management"
        description="Create and publish surveys, and manage the templates they are built from"
        breadcrumbs={[{ label: 'Home', path: '/dashboard' }, { label: 'Survey Management' }]}
        actions={
          <Button
            htmlType="button"
            appearance="primary"
            size="middle"
            onClick={() => openWizard('create')}
            icon={<Plus className="w-4 h-4" />}
          >
            Create Survey
          </Button>
        }
      />

      {hasError ? (
        <div className="bg-[var(--color-bg-base)] border border-[var(--color-border-primary)] rounded-card shadow-[var(--shadow-sm)] text-center px-6 py-14">
          <h3 className="text-heading-sm text-[var(--color-text-primary)] mb-1">Failed to load surveys</h3>
          <p className="text-body-sm text-[var(--color-text-secondary)] mb-5">
            Something went wrong while fetching surveys. Please try again.
          </p>
          <Button appearance="primary" size="small" onClick={() => refreshAll()}>
            Retry
          </Button>
        </div>
      ) : (
        <>
          <div className="mb-5 sm:mb-6">
            {statsLoading ? (
              <StatCardSkeleton count={4} />
            ) : (
              <StatsCards
                stats={statistics}
                cards={CREATION_STATS_CARDS}
                variant="simple-icon"
                valueOnTop
              />
            )}
          </div>

          <div className="mb-5 sm:mb-6">
            <TabSelector
              tabs={managementTabs}
              active={activeTab}
              onChange={(key: ManagementTabKey) => setActiveTab(key)}
            />
          </div>

          {activeTab === 'published' && (
            <div className="mb-5 sm:mb-6">
              <DataTableCard
                title="Published Surveys"
                count={publishedTotal}
                toolbar={
                  <>
                    <SearchInput
                      value={searchInput}
                      onChange={updateSearch}
                      placeholder="Search surveys..."
                      className="w-full sm:w-[220px]"
                    />
                    <DatePicker
                      value={filters.fromDate ? dayjs(filters.fromDate) : null}
                      onChange={(d) => updateFromDate(d ? d.format('YYYY-MM-DD') : null)}
                      placeholder="Start date"
                      className="w-full sm:w-[180px]"
                    />
                    <DatePicker
                      value={filters.toDate ? dayjs(filters.toDate) : null}
                      onChange={(d) => updateToDate(d ? d.format('YYYY-MM-DD') : null)}
                      placeholder="End date"
                      className="w-full sm:w-[180px]"
                    />
                    <ColumnSelector
                      columns={publishedColumns}
                      visibleKeys={pubVisibleKeys}
                      onToggle={toggleKeys(setPubVisibleKeys)}
                    />
                  </>
                }
                pagination={
                  publishedTotal > publishedLimit ? (
                    <Pagination2
                      total={publishedTotal}
                      page={publishedPage}
                      pageSize={publishedLimit}
                      pageSizeOptions={[5, 10, 20, 50]}
                      onPageChange={setPublishedPage}
                      onPageSizeChange={(l) => setPublishedLimit(l)}
                    />
                  ) : undefined
                }
              >
                <DataTable
                  columns={pubVisible}
                  rows={publishedSurveys}
                  rowKey={(r) => r._id}
                  loading={publishedLoading}
                  skeleton={<TableSkeleton rows={publishedLimit} columns={pubVisible.length} />}
                  emptyMessage="No published surveys found"
                  responsive="scroll"
                />
              </DataTableCard>
            </div>
          )}

          {activeTab === 'drafts' && (
            <div className="mb-5 sm:mb-6">
              <DataTableCard
                title="Draft Surveys"
                count={draftsTotal}
                toolbar={
                  <>
                    <SearchInput
                      value={searchInput}
                      onChange={updateSearch}
                      placeholder="Search drafts..."
                      className="w-full sm:w-[220px]"
                    />
                    <ColumnSelector
                      columns={draftColumns}
                      visibleKeys={draftVisibleKeys}
                      onToggle={toggleKeys(setDraftVisibleKeys)}
                    />
                  </>
                }
                pagination={
                  draftsTotal > draftsLimit ? (
                    <Pagination2
                      total={draftsTotal}
                      page={draftsPage}
                      pageSize={draftsLimit}
                      pageSizeOptions={[5, 10, 20, 50]}
                      onPageChange={setDraftsPage}
                      onPageSizeChange={(l) => setDraftsLimit(l)}
                    />
                  ) : undefined
                }
              >
                <DataTable
                  columns={draftVisible}
                  rows={drafts}
                  rowKey={(r) => r._id}
                  loading={draftsLoading}
                  skeleton={<TableSkeleton rows={draftsLimit} columns={draftVisible.length} />}
                  emptyMessage="No draft surveys found"
                  responsive="scroll"
                />
              </DataTableCard>
            </div>
          )}

          {/* Figma Template Library — always visible below the tabs */}
          <DataTableCard
            title="Template Library"
            count={templatesTotal}
            toolbar={
              <ColumnSelector
                columns={templateColumns}
                visibleKeys={tplVisibleKeys}
                onToggle={toggleKeys(setTplVisibleKeys)}
              />
            }
            pagination={
              templatesTotal > templatesLimit ? (
                <Pagination2
                  total={templatesTotal}
                  page={templatesPage}
                  pageSize={templatesLimit}
                  pageSizeOptions={[5, 10, 20, 50]}
                  onPageChange={setTemplatesPage}
                  onPageSizeChange={(l) => setTemplatesLimit(l)}
                />
              ) : undefined
            }
          >
            <DataTable
              columns={tplVisible}
              rows={templates}
              rowKey={(r) => r._id}
              loading={templatesLoading}
              skeleton={<TableSkeleton rows={templatesLimit} columns={tplVisible.length} />}
              emptyMessage="No templates found"
              responsive="scroll"
            />
          </DataTableCard>
        </>
      )}

      {wizard && (
        <CreateSurveyModal
          isOpen
          onClose={() => setWizard(null)}
          mode={wizard.mode}
          entityType={wizard.entityType}
          entityId={wizard.entityId}
        />
      )}

      <DeleteConfirmModal
        isOpen={deleteModal?.isOpen ?? false}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        loading={!!actionLoading}
        itemType={deleteModal?.type === 'published' ? 'survey' : deleteModal?.type as DeleteItemType}
        itemTitle={deleteModal?.title}
      />

      <DuplicateTemplateModal
        isOpen={duplicateModal?.isOpen ?? false}
        onClose={closeDuplicateModal}
        onConfirm={confirmDuplicate}
        loading={!!actionLoading}
        template={templates.find((t) => t._id === duplicateModal?.templateId) || null}
      />
    </div>
  );
};

export default SurveyManagement;
